import { api } from './client';

export type KioskMode =
  | 'display'
  | 'gateway'
  | 'hybrid'
  | 'meeting_room'
  | 'room_display'
  | 'patient_experience';

export interface Kiosk {
  id: string;
  name: string;
  displayCode?: string | null;
  zoneId?: string | null;
  zone?: { id: string; name: string; site?: { id: string; name: string } | null } | null;
  isActive: boolean;
  lastSeenAt?: string | null;
  serialNumber?: string | null;
  hardwareModel?: string | null;
  displayConfig?: Record<string, any>;
  deviceHealth?: Record<string, any> | null;
  currentVersion?: string | null;
  updateStatus?: Record<string, any> | null;
}

export interface KioskScreenshot {
  data: string | null;
  format: string | null;
  screenshotAt: string | null;
}

export const kioskApi = {
  list: async (): Promise<Kiosk[]> => {
    const response = await api.get('/admin/kiosks');
    return response.data;
  },

  getById: async (id: string): Promise<Kiosk> => {
    const response = await api.get(`/admin/kiosks/${id}`);
    return response.data;
  },

  create: async (data: {
    name: string;
    orgId?: string;
    zoneId?: string;
    serialNumber?: string;
    hardwareModel?: string;
    displayConfig?: Record<string, any>;
  }): Promise<Kiosk> => {
    const response = await api.post('/admin/kiosks', data);
    return response.data;
  },

  update: async (id: string, data: Partial<Kiosk>): Promise<Kiosk> => {
    const response = await api.patch(`/admin/kiosks/${id}`, data);
    return response.data;
  },

  regenerateCode: async (id: string): Promise<Kiosk> => {
    const response = await api.post(`/admin/kiosks/${id}/regenerate-code`);
    return response.data;
  },

  sendCommand: async (
    id: string,
    command: { type: string; payload?: Record<string, any> },
  ): Promise<{ ok: boolean; type: string }> => {
    const response = await api.post(`/admin/kiosks/${id}/command`, command);
    return response.data;
  },

  requestScreenshot: async (id: string): Promise<{ ok: boolean; type: string }> => {
    const response = await api.post(`/admin/kiosks/${id}/command`, { type: 'screenshot' });
    return response.data;
  },

  getScreenshot: async (id: string): Promise<KioskScreenshot> => {
    const response = await api.get(`/admin/kiosks/${id}/screenshot`);
    return response.data;
  },

  otaUpdate: async (
    id: string,
    data: { apkUrl: string; version: string; notes?: string; expectedSha256?: string },
  ): Promise<{ ok: boolean }> => {
    const response = await api.post(`/admin/kiosks/${id}/ota-update`, data);
    return response.data;
  },

  cancelOtaUpdate: async (id: string): Promise<{ ok: boolean }> => {
    const response = await api.post(`/admin/kiosks/${id}/cancel-ota`);
    return response.data;
  },
};
